import axios from 'axios';
import type { Academy, ApiResponse } from '@court-sync/shared';
import { PUBLIC_ACADEMY_BRANDED_SUBDOMAIN_CODE, orgPublicBaseUrl } from '@court-sync/shared';
import { buildAcademyAbsoluteUrl } from './academyPaths';
import { isApiError } from '../services/apiClient';

export type BrandedSubdomainBlockPayload = {
  code: typeof PUBLIC_ACADEMY_BRANDED_SUBDOMAIN_CODE;
  slug: string;
  subdomain: string;
  subscriptionTier?: Academy['subscriptionTier'];
};

export function parseBrandedSubdomainFromApiBody(body: unknown): BrandedSubdomainBlockPayload | null {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const data = (body as ApiResponse<BrandedSubdomainBlockPayload>).data;
  if (
    data?.code === PUBLIC_ACADEMY_BRANDED_SUBDOMAIN_CODE &&
    typeof data.subdomain === 'string' &&
    data.subdomain.trim() &&
    typeof data.slug === 'string'
  ) {
    return data;
  }
  return null;
}

export function parseBrandedSubdomainBlock(error: unknown): BrandedSubdomainBlockPayload | null {
  if (axios.isAxiosError(error)) {
    return parseBrandedSubdomainFromApiBody(error.response?.data);
  }

  if (isApiError(error) && error.responseData) {
    return parseBrandedSubdomainFromApiBody(error.responseData);
  }

  return null;
}

export function buildBrandedSubdomainRedirectUrl(
  block: BrandedSubdomainBlockPayload,
  subpath: string
): string {
  return buildAcademyAbsoluteUrl(
    {
      slug: block.slug,
      subdomain: block.subdomain,
      subdomainEnabled: true,
      subscriptionTier: block.subscriptionTier,
    },
    subpath
  );
}

export function redirectToBrandedSubdomain(block: BrandedSubdomainBlockPayload, subpath: string): void {
  if (typeof window === 'undefined') return;
  window.location.replace(buildBrandedSubdomainRedirectUrl(block, subpath));
}

export function pathSubpathForAcademySlug(pathname: string, slug: string): string {
  const prefix = `/academy/${slug}`;
  if (pathname === prefix) return '';
  if (pathname.startsWith(`${prefix}/`)) {
    return pathname.slice(prefix.length);
  }
  return '';
}

export function pathSubpathForOrgSlug(pathname: string, slug: string): string {
  const prefix = `/club-academy/${slug}`;
  if (pathname === prefix) return '';
  if (pathname.startsWith(`${prefix}/`)) {
    return pathname.slice(prefix.length);
  }
  return '';
}

export function buildOrgBrandedSubdomainRedirectUrl(
  block: Pick<BrandedSubdomainBlockPayload, 'slug' | 'subdomain'>,
  subpath: string
): string {
  return orgPublicBaseUrl(
    { slug: block.slug, subdomain: block.subdomain, subdomainEnabled: true },
    subpath,
    undefined,
    'path',
    true
  );
}

export function redirectToOrgBrandedSubdomain(
  block: Pick<BrandedSubdomainBlockPayload, 'slug' | 'subdomain'>,
  subpath: string
): void {
  if (typeof window === 'undefined') return;
  window.location.replace(buildOrgBrandedSubdomainRedirectUrl(block, subpath));
}
